import { Input, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ChangeEvent, useEffect, useRef, useState } from "react";

interface MoneyInputProps {
  label: string;
  value?: number;
  onChange: (value: number | undefined) => void;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

export function MoneyInput(props: MoneyInputProps) {
  const {
    label,
    value,
    onChange,
    disabled = false,
    error = false,
    placeholder = "Enter amount",
    min = 0,
    max = 500,
    step = 0.01,
  } = props;

  const [inputValue, setInputValue] = useState<string>(isDefined(value) ? String(value) : "");
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!isInternalChange.current) {
      setInputValue(isDefined(value) ? String(value) : "");
    }

    isInternalChange.current = false;
  }, [value]);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newInputValue = event.target.value;
    isInternalChange.current = true;
    setInputValue(newInputValue);

    if (newInputValue === "" || newInputValue === "-") {
      onChange(undefined);
      return;
    }

    const numericValue = Number(newInputValue);

    if (Number.isNaN(numericValue)) {
      return;
    }

    onChange(numericValue);
  }

  return (
    <Stack spacing={2}>
      <Text semibold variant="body1">
        {label}
      </Text>
      <Input
        fullWidth
        type="number"
        placeholder={placeholder}
        value={inputValue}
        error={error}
        disabled={disabled}
        iconType="coin"
        inputMode="decimal"
        slotProps={{
          input: {
            step,
            min,
            ...(isDefined(max) && { max }),
          },
        }}
        onChange={(event) => {
          handleChange(event);
        }}
      />
    </Stack>
  );
}
